import React, { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import DashboardsEngine from '@brandwatch/dashboards-engine';
import { IDashboard, TStyling } from '@brandwatch/dashboards-engine/src/decs';
import { bcrToDETransform, IBCRDashboardConfiguration } from '@brandwatch/dashboards-engine/dist/helpers/transform';
import { getFormattedMetadata } from '../helpers/metadataFormatter';
import '../App.css';

interface IDashboardProps {
	baseUrl: string;
	dashboardJSON: IDashboardResource;
	minutesSinceLastFetch?: number;
	password: string;
}

type TBCRDashboardJSON = IBCRDashboardConfiguration['dashboard'];

type TApiConfiguration = IBCRDashboardConfiguration['apiConfiguration'] & {
	projectId: number;
};

interface IDashboardResource extends TBCRDashboardJSON {
	resourceCreatedAt?: string;
	resourceExpiresAt?: string;
	resourceType: 'dashboard-export-json' | 'live-dashboard-meta' | 'dashboard-snapshot-json';
}

const Dashboard = (props: IDashboardProps): ReactElement => {
	const { baseUrl, dashboardJSON, minutesSinceLastFetch, password } = props;
	const { resourceCreatedAt = '', dashboardTimezone, resourceExpiresAt } = dashboardJSON;
	const { sharedDashboardUuid } = useParams<{ sharedDashboardUuid: string; }>();

	const options = {
		footer: {
			show: true
		},
		header: {
			description: dashboardJSON.resourceType === 'live-dashboard-meta' ? `This dashboard was updated ${minutesSinceLastFetch} minute${minutesSinceLastFetch === 1 ? '' : 's'} ago` :
				getFormattedMetadata({
					creationDate: resourceCreatedAt || '',
					expirationDate: resourceExpiresAt || '',
					timezone: dashboardTimezone || 'Etc/UTC'
				}),
			fixed: true,
			show: true
		}
	};

	// dashboard json from live-dashboard-meta and dashboard-snapshot-json resources are filtered. But DE expects id & projectId.
	// also, zephyrus expects a projectId prop either from the api configuration or per request
	const PLACEHOLDER_ID = -1;

	if (!dashboardJSON.id) {
		dashboardJSON.id = PLACEHOLDER_ID;
	}
	if (!dashboardJSON.projectId) {
		dashboardJSON.projectId = PLACEHOLDER_ID;
	}

	const { hostname, port, protocol } = new URL(baseUrl);
	const apiAuthHeader = `basic ${btoa(`:${password}`)}`;
	const apiConfiguration: TApiConfiguration = {
		apiAuthHeader,
		apiHostname: hostname,
		apiPort: port,
		apiProtocol: protocol,
		pathName: `/dashboard/${sharedDashboardUuid}`,
		projectId: PLACEHOLDER_ID
	};

	const dashboardConfiguration: IBCRDashboardConfiguration = {
		apiConfiguration,
		dashboard: dashboardJSON,
		resourceCreatedAt,
		throttleInterval: 'minute'
	};

	const transformedDashboardJSON: IDashboard = bcrToDETransform(dashboardConfiguration);

	const customStyling = {
		'.dashboards-engine-app-root': {
			'min-height': '100vh',
		}
	} as TStyling;

	transformedDashboardJSON.styling = customStyling;

	return (
		<DashboardsEngine
			dashboard={transformedDashboardJSON}
			options={options}
		/>
	);
};

export { Dashboard };
